// The run ledger, newest first: when, who, how it ended, what it raised.

import type { RunRead } from '../../services/kbEffectiveness';
import { whenUtc } from './format';
import styles from '../KBEffectivenessPage.module.css';

interface Props {
  runs: RunRead[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function severityCounts(run: RunRead): string {
  const s = 'findings_by_severity' in run.headline ? run.headline.findings_by_severity : undefined;
  if (!s) return '–';
  return `${s.high ?? 0} / ${s.medium ?? 0} / ${s.low ?? 0}`;
}

export function RunHistory({ runs, selectedId, onSelect }: Props) {
  return (
    <section className={styles.panel} aria-label="Run history">
      <div className={styles.panelHead}>
        <h2>Runs</h2>
        <span className={styles.meta}>newest first · click a completed run to view its report</span>
      </div>
      {runs.length === 0 ? (
        <p className={styles.empty}>No run yet. The weekly beat schedules one every Monday; or run it now.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>scheduled</th>
                <th>trigger</th>
                <th>state</th>
                <th className={styles.num}>window</th>
                <th>finished</th>
                <th className={styles.num}>high / med / low</th>
                <th className={styles.num}>flags</th>
                <th>note</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const flags = run.detail.red_flags?.filter((f) => f.flag_id).length ?? 0;
                const warnings = run.detail.warnings ? Object.keys(run.detail.warnings) : [];
                const note = run.error
                  ? `${run.failed_step ?? 'failed'}: ${run.error.split('\n')[0]}`
                  : warnings.length
                    ? `optional pass skipped: ${warnings.join(', ')}`
                    : '';
                const clickable = run.state === 'completed';
                return (
                  <tr
                    key={run.id}
                    className={`${clickable ? styles.clickable : ''} ${run.id === selectedId ? styles.selectedRow : ''}`}
                    onClick={clickable ? () => onSelect(run.id) : undefined}
                    data-state={run.state}
                  >
                    <td>{whenUtc(run.scheduled_at)}</td>
                    <td>{run.trigger === 'manual' ? `manual (${run.triggered_by_email ?? '?'})` : 'weekly beat'}</td>
                    <td><span className={`${styles.state} ${styles[`state_${run.state}`]}`}>{run.state}</span></td>
                    <td className={styles.num}>{run.window_days}d</td>
                    <td>{whenUtc(run.finished_at)}</td>
                    <td className={styles.num}>{severityCounts(run)}</td>
                    <td className={styles.num}>{run.state === 'completed' ? flags : '–'}</td>
                    <td className={styles.note} title={run.error ?? undefined}>{note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
